import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Headphones, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'wouter';

import { BillingChargeActions } from '@/features/billing/billing-charge-actions';
import {
  customerBillingQueryKey,
  customerBillingService,
} from '@/services/customer-billing.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FullPageLoader } from '@/components/feedback/full-page-loader';
import { useAuth } from '@/contexts/auth-context';

const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const date = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' });

export function FinancialAccessGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const owner = user?.role === 'OWNER';
  const query = useQuery({
    queryKey: customerBillingQueryKey,
    queryFn: customerBillingService.summary,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled: owner,
  });
  if (!owner) return children;
  if (query.isLoading) return <FullPageLoader />;
  if (query.isError || !query.data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Não foi possível validar sua licença</CardTitle>
            <CardDescription>Tente novamente ou acesse o suporte.</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Button onClick={() => void query.refetch()}>
              <RefreshCw /> Tentar novamente
            </Button>
            <Button asChild variant="outline">
              <Link href="/suporte">Suporte</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!query.data.accessBlocked) return children;
  const invoice = query.data.blockingInvoice;
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4 text-slate-950">
      <Card className="w-full max-w-2xl overflow-hidden border-0 shadow-2xl">
        <div className="bg-red-600 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <span className="flex size-11 items-center justify-center rounded-xl bg-white/15">
              <AlertTriangle className="size-6" />
            </span>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-red-100">
                Acesso temporariamente restrito
              </p>
              <h1 className="text-2xl font-bold">Licença vencida</h1>
            </div>
          </div>
        </div>
        <CardContent className="space-y-6 p-6 sm:p-8">
          <p className="text-slate-600">
            Regularize a mensalidade para reativar o ChefiaPro. Após a confirmação do provedor
            financeiro, o acesso será liberado automaticamente.
          </p>
          {invoice ? (
            <div className="grid gap-3 rounded-xl border bg-slate-50 p-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Vencimento
                </p>
                <p className="mt-1 text-lg font-bold">{date.format(new Date(invoice.dueDate))}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Valor
                </p>
                <p className="mt-1 text-lg font-bold">{money.format(invoice.amount)}</p>
              </div>
            </div>
          ) : null}
          {invoice ? <BillingChargeActions invoice={invoice} /> : null}
          <div className="flex flex-wrap gap-2 border-t pt-5">
            <Button asChild variant="outline">
              <Link href="/portal-financeiro">Abrir Portal Financeiro</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link href="/suporte">
                <Headphones /> Contatar suporte
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
